import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageCircle, 
  Users,
  Car,
  Shield,
  CheckCircle,
  Send,
  Calendar,
  Navigation
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleType: '',
    pickupLocation: '',
    dropLocation: '',
    pickupDate: '',
    pickupTime: '',
    passengers: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.vehicleType) newErrors.vehicleType = 'Please select a vehicle';
    if (!formData.pickupLocation.trim()) newErrors.pickupLocation = 'Pickup location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Contact Form Data:', formData);
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      vehicleType: '',
      pickupLocation: '',
      dropLocation: '',
      pickupDate: '',
      pickupTime: '',
      passengers: '',
      message: ''
    });
    
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      <Helmet>
        <title>Contact Urbania Nava Cabs | Book Force Urbania & Innova in Bangalore - Call +91 99647 58999</title>
        <meta 
          name="description" 
          content="Contact Urbania Nava Cabs for Force Urbania, Innova, Tempo Traveller & Volvo Bus booking in Bangalore. 24/7 service. Call 📞 +91 99647 58999 for instant booking."
        />
        <meta name="keywords" content="contact urbania nava cabs, force urbania booking contact, innova crysta rental bangalore, tempo traveller booking number, volvo bus hire contact, cab service bangalore contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Urbania Nava Cabs",
            "description": "Book Force Urbania, Innova, Tempo Traveller & Volvo Bus in Bangalore",
            "url": "https://urbanianavacabs.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "Urbania Nava Cabs",
              "telephone": "+919964758999",
              "email": "info@urbanianavacabs.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Bangalore",
                "addressRegion": "Karnataka",
                "addressCountry": "India"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              Contact Urbania Nava Cabs
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Get instant booking for Force Urbania, Innova, Tempo Traveller & Volvo Buses in Bangalore
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              {/* Quick Contact Cards */}
              <div className="bg-white rounded-lg shadow p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-blue-600" />
                  Instant Booking
                </h2>
                
                <div className="space-y-3">
                  <a 
                    href="tel:+919964758999" 
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="bg-green-100 p-2 rounded-lg mr-3">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">+91 99647 58999</div>
                        <div className="text-sm text-gray-600">Primary Number</div>
                      </div>
                    </div>
                  </a>

                  <a 
                    href="tel:+919916788688" 
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3">
                        <Phone className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">+91 99647 58999</div>
                        <div className="text-sm text-gray-600">Alternate Number</div>
                      </div>
                    </div>
                  </a>

                  <a 
                    href="https://wa.me/919964758999" 
                    className="flex items-center justify-between p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <div className="flex items-center">
                      <MessageCircle className="w-5 h-5 mr-3" />
                      <div>
                        <div className="font-bold">WhatsApp Booking</div>
                        <div className="text-sm opacity-90">Instant response</div>
                      </div>
                    </div>
                    <div className="text-xs bg-white/20 px-2 py-1 rounded">Recommended</div>
                  </a>
                </div>
              </div>

              {/* Business Info */}
              <div className="bg-white rounded-lg shadow p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Business Information</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">Service Areas</div>
                      <div className="text-sm text-gray-600">
                        All Bangalore areas including Whitefield, Electronic City, Koramangala, 
                        Indiranagar, HSR Layout, JP Nagar, Airport Zone, and surrounding areas
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">Operating Hours</div>
                      <div className="text-sm text-gray-600">24/7 Service Available</div>
                      <div className="text-xs text-gray-500">365 days a year</div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">Email Address</div>
                      <a 
                        href="mailto:urbanianavacabs@gmail.com" 
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        urbanianavacabs@gmail.com
                      </a>
                      <div className="text-xs text-gray-500">Response within 2 hours</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-gray-700">Fully Insured Vehicles</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-700">Experienced Professional Drivers</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-gray-700">24/7 Customer Support</span>
                  </div>
                  <div className="flex items-center">
                    <Car className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-700">Well-maintained Fleet</span>
                  </div>
                  <div className="flex items-center">
                    <Navigation className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-700">GPS Equipped Vehicles</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-5 md:p-6">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Booking Request Submitted!</h3>
                    <p className="text-gray-600 mb-4">
                      We have received your booking request. Our team will contact you within 15 minutes.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-sm text-gray-700 mb-2">
                        For immediate assistance, call us directly:
                      </p>
                      <a 
                        href="tel:+919964758999" 
                        className="inline-flex items-center text-blue-600 font-bold"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        +91 99647 58999
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Quick Booking Request
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Fill the form below and our team will contact you within 15 minutes
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.name ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`}
                            placeholder="Enter your full name"
                          />
                          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.phone ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`}
                            placeholder="10-digit mobile number"
                          />
                          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            placeholder="your@email.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Vehicle Type *
                          </label>
                          <select
                            name="vehicleType"
                            value={formData.vehicleType}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.vehicleType ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`}
                          >
                            <option value="">Select Vehicle</option>
                            <option value="force-urbania-16">Force Urbania 16 Seater</option>
                            <option value="force-urbania-12">Force Urbania 12 Seater</option>
                            <option value="force-urbania-9">Force Urbania 9 Seater</option>
                            <option value="innova-crysta">Toyota Innova Crysta</option>
                            <option value="innova-hycross">Toyota Innova Hycross</option>
                            <option value="tempo-12">Tempo Traveller 12 Seater</option>
                            <option value="tempo-9">Tempo Traveller 9 Seater</option>
                            <option value="volvo-50">Volvo Bus 50 Seater</option>
                            <option value="volvo-40">Volvo Bus 40 Seater</option>
                            <option value="volvo-30">Volvo Bus 30 Seater</option>
                            <option value="volvo-20">Volvo Bus 20 Seater</option>
                            <option value="ertiga">Maruti Suzuki Ertiga</option>
                            <option value="sedan">Sedan</option>                            
                          </select>
                          {errors.vehicleType && <p className="text-red-500 text-xs mt-1">{errors.vehicleType}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pickup Location *
                          </label>
                          <input
                            type="text"
                            name="pickupLocation"
                            value={formData.pickupLocation}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.pickupLocation ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`}
                            placeholder="Enter pickup location"
                          />
                          {errors.pickupLocation && <p className="text-red-500 text-xs mt-1">{errors.pickupLocation}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Drop Location
                          </label>
                          <input
                            type="text"
                            name="dropLocation"
                            value={formData.dropLocation}
                            onChange={handleChange}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            placeholder="Enter drop location"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pickup Date
                          </label>
                          <div className="flex items-center">
                            <Calendar className="w-5 h-5 text-gray-400 absolute ml-3" />
                            <input
                              type="date"
                              name="pickupDate"
                              value={formData.pickupDate}
                              onChange={handleChange}
                              min={today}
                              className="w-full pl-10 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pickup Time
                          </label>
                          <input
                            type="time"
                            name="pickupTime"
                            value={formData.pickupTime}
                            onChange={handleChange}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Passengers
                          </label>
                          <input
                            type="number"
                            name="passengers"
                            value={formData.passengers}
                            onChange={handleChange}
                            min="1"
                            max="50"
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            placeholder="Number of passengers"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Additional Message (Optional)
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                          placeholder="Any special requirements or details..."
                        ></textarea>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center">
                          <Phone className="w-5 h-5 text-blue-600 mr-3" />
                          <div>
                            <p className="text-sm text-gray-700">
                              For urgent bookings or immediate assistance, call us directly:
                            </p>
                            <a 
                              href="tel:+919964758999" 
                              className="text-blue-600 font-bold hover:text-blue-800"
                            >
                              +91 99647 58999
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow hover:shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5 mr-2" />
                              Submit Booking Request
                            </>
                          )}
                        </button>
                        
                        <a 
                          href="https://wa.me/919964758999" 
                          className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-all shadow hover:shadow-lg flex items-center justify-center"
                        >
                          <MessageCircle className="w-5 h-5 mr-2" />
                          WhatsApp Instant Booking
                        </a>
                      </div>

                      <p className="text-xs text-gray-500 text-center">
                        By submitting this form, you agree to our terms and conditions. We respect your privacy.
                      </p>
                    </form>
                  </>
                )}
              </div>

              {/* Vehicle Categories */}
              <div className="mt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Popular Vehicle Categories</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <a 
                    href="/force-urbania-16-seater-bangalore"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Force Urbania</div>
                    <div className="text-xs text-gray-600">9/12/16 Seater</div>
                  </a>
                  <a 
                    href="/innova-crysta-booking-bangalore"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Innova Crysta</div>
                    <div className="text-xs text-gray-600">7 Seater Premium</div>
                  </a>
                  <a 
                    href="/tempo-traveller-12-seater-booking"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Tempo Traveller</div>
                    <div className="text-xs text-gray-600">9/12 Seater AC</div>
                  </a>
                  <a 
                    href="/volvo-bus-rental-bangalore"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Volvo Buses</div>
                    <div className="text-xs text-gray-600">20-50 Seater</div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Frequently Asked Questions
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">How quickly can I get a vehicle?</h3>
                <p className="text-gray-600 text-sm">
                  For immediate requirements, we can provide vehicles within 30-60 minutes in Bangalore city. 
                  For advance bookings, please book at least 2 hours in advance.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">What payment methods do you accept?</h3>
                <p className="text-gray-600 text-sm">
                  We accept cash, UPI, credit/debit cards, net banking, and corporate billing. 
                  For corporate clients, we provide monthly billing with GST invoices.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">Do you provide drivers?</h3>
                <p className="text-gray-600 text-sm">
                  Yes, all our vehicles come with experienced, professional drivers who are 
                  well-trained and familiar with Bangalore routes and traffic patterns.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">What is your cancellation policy?</h3>
                <p className="text-gray-600 text-sm">
                  Free cancellation up to 2 hours before pickup time. For cancellations within 
                  2 hours, a nominal cancellation charge may apply depending on vehicle type.
                </p>
              </div>
            </div>
          </div>

          {/* Service Areas */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Service Areas in Bangalore
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">East Bangalore</div>
                <div className="text-xs text-gray-600 mt-1">Whitefield, Marathahalli, KR Puram, Indiranagar</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">South Bangalore</div>
                <div className="text-xs text-gray-600 mt-1">JP Nagar, Banashankari, HSR, Koramangala</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">North Bangalore</div>
                <div className="text-xs text-gray-600 mt-1">Hebbal, Yelahanka, Malleshwaram, Rajajinagar</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">Central & West</div>
                <div className="text-xs text-gray-600 mt-1">MG Road, Brigade Road, Yeshwanthpur, Peenya</div>
              </div>
            </div>
            
            <div className="text-center mt-6">
              <p className="text-gray-700">
                We service <strong>all areas of Bangalore</strong> including Kempegowda International Airport (BLR)
              </p>
            </div>


   <div className="mt-16">
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-indigo-100">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Find Our Location in Bangalore</h2>
              <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
                Visit our office at Kengeri, Bangalore for car rental bookings and inquiries. 
                We're conveniently located with easy access to major routes.
              </p>
              
             <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-indigo-100">
 <iframe
          src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d7778.135452500239!2d77.47272133827211!3d12.90336659951391!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sPhase%202%2C%20royal%20elite%2C%20129%2C%205th%20Cross%20Rd%2C%20Layout%2C%20Kengeri!5e0!3m2!1sen!2sin!4v1768931732307!5m2!1sen!2sin"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Urbania Nava Cabs Office Location - Kengeri, Bangalore"
          className="w-full"
        />
        </div>
              
              <div className="mt-6 text-center">
                <p className="text-gray-600 mb-4">
                  <strong>Address:</strong> SHIVAKRUPA 129/1, 5TH Cross Rd, Kengeri, Bengaluru, Karnataka 560060
                </p>
                <a
                  href="https://maps.app.goo.gl/LgCx1a4uGQNuZJmm6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Get Directions on Google Maps
                </a>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;